// Cave84 — Vintage Charts service (Phase 3 Week 11)
// Spec §4.11 (F11). Read/write layer for the vintage_charts collection.
// Document IDs use the composite key convention: "{region}_{year}"
// e.g. "Burgundy_Red_2015"

import {
  addDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import type { VintageChart } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection, typedDoc } from './firestore';

const COLLECTION = 'vintage_charts';

function col() {
  return typedCollection<VintageChart>(COLLECTION);
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Fetch every record in the collection — used to build the full matrix. */
export async function listAllVintageCharts(): Promise<VintageChart[]> {
  const snap = await getDocs(query(col(), orderBy('year', 'desc')));
  return snap.docs.map((d) => d.data());
}

/** Fetch all records for a single region, newest first. */
export async function listVintageChartsByRegion(region: string): Promise<VintageChart[]> {
  const snap = await getDocs(
    query(col(), where('region', '==', region), orderBy('year', 'desc'))
  );
  return snap.docs.map((d) => d.data());
}

/** Fetch a single record by composite ID: "{region}_{year}". */
export async function getVintageChart(id: string): Promise<VintageChart | null> {
  const snap = await getDoc(typedDoc<VintageChart>(COLLECTION, id));
  if (!snap.exists()) return null;
  return snap.data();
}

/**
 * Fetch the single record for a (region, year) pair.
 * Returns null if not seeded yet.
 */
export async function getVintageChartByRegionYear(
  region: string,
  year: number
): Promise<VintageChart | null> {
  const id = `${region}_${year}`;
  return getVintageChart(id);
}

// ---------------------------------------------------------------------------
// Mutations (Owner only — enforced by Firestore rules)
// ---------------------------------------------------------------------------

export type VintageChartUpdateInput = Pick<
  VintageChart,
  | 'score'
  | 'quality_label'
  | 'weather_narrative_he'
  | 'style_character'
  | 'drinking_status'
  | 'notable_producers_overperformed'
  | 'comparison_to_adjacent'
>;

/**
 * Owner manual override of a single vintage chart entry.
 * Marks generated_by = 'manual' and refreshes last_updated.
 */
export async function updateVintageChart(
  region: string,
  year: number,
  input: Partial<VintageChartUpdateInput>
): Promise<void> {
  const id = `${region}_${year}`;
  const ref = doc(db, COLLECTION, id);
  const { updateDoc } = await import('firebase/firestore');
  await updateDoc(ref, {
    ...input,
    generated_by: 'manual',
    last_updated: serverTimestamp(),
  });
}

/**
 * Enqueue a vintage-chart-year generation job for a (region, year) pair.
 * The CLI runner picks this up and populates the record via the wine-expert skill.
 */
export async function enqueueVintageChartYear(
  region: string,
  year: number
): Promise<string> {
  const queueCol = typedCollection<{
    id: string;
    entity_type: string;
    entity_id: string;
    priority: string;
    status: string;
    requested_at: ReturnType<typeof serverTimestamp>;
    trigger_reason: string;
  }>('generation_queue');

  const ref = await addDoc(queueCol, {
    id: '',                          // overwritten by converter
    entity_type: 'vintage_chart_year',
    entity_id: `${region}_${year}`,
    priority: 'normal',
    status: 'pending',
    requested_at: serverTimestamp(),
    trigger_reason: 'owner_add_year',
  } as never);

  return ref.id;
}
